import React, {useState, useCallback, useEffect, useContext} from 'react';
import FirebaseContext from "./FirebaseContext";
import { Expense, Exchange } from '../../types';
import { SheetContextState } from './types';

const SheetContext = React.createContext<SheetContextState | null>(null);
export default SheetContext;

const LS_KEY = {
  expenses: 'expenses',
  exchanges: 'exchanges',
  categories: 'categories',
  accounts: 'accounts',
}
const EXPENSES_ROW_LIMIT = 30;
const EXCHANGES_ROW_LIMIT = 10;
const API_ROOT = import.meta.env.VITE_API_URL;

interface SheetProviderProps {
  children: React.ReactNode;
}

export function SheetProvider({children}: SheetProviderProps) {
  const {user, userLoading, firebaseRequest, getIdToken} = useContext(FirebaseContext);
  const initialExpenses: Expense[] = JSON.parse(localStorage.getItem(LS_KEY.expenses) || '[]') || [];
  const initialExchanges: Exchange[] = JSON.parse(localStorage.getItem(LS_KEY.exchanges) || '[]') || [];
  const initialCategories: string[] = JSON.parse(localStorage.getItem(LS_KEY.categories) || '[]') || [];
  const initialAccounts: string[] = JSON.parse(localStorage.getItem(LS_KEY.accounts) || '[]') || ['Cash'];
  const [migrating, setMigrating] = useState<boolean>(false);
  const [migrationMessage, setMigrationMessage] = useState<string>('');
  const [expenses, setExpenses] = useState<Expense[]>(initialExpenses);
  const [exchanges, setExchanges] = useState<Exchange[]>(initialExchanges);
  const [categories, setCategories] = useState<string[]>(initialCategories);
  const [accounts, setAccounts] = useState<string[]>(initialAccounts);
  const [expensesLoading, setExpensesLoading] = useState<boolean>(!initialExpenses || !initialExpenses.length);
  const [exchangesLoading, setExchangesLoading] = useState<boolean>(!initialExchanges || !initialExchanges.length);

  const stripRowData = (e: Expense | Exchange): Expense | Exchange => {
    const clone = {...e};
    delete clone.row;
    return clone;
  }

  // region Change Events
  // Update expenses in storage when updated
  useEffect(() => {
    const strippedData = expenses.map(stripRowData);
    localStorage.setItem(LS_KEY.expenses, JSON.stringify(strippedData));
  }, [expenses]);
  // Update exchanges in storage when updated
  useEffect(() => {
    const strippedData = exchanges.map(stripRowData);
    localStorage.setItem(LS_KEY.exchanges, JSON.stringify(strippedData));
  }, [exchanges]);
  // Update categories in storage when updated
  useEffect(() => {
    localStorage.setItem(LS_KEY.categories, JSON.stringify(categories));
  }, [categories]);
  // Update accounts in storage when updated
  useEffect(() => {
    localStorage.setItem(LS_KEY.accounts, JSON.stringify(accounts));
  }, [accounts]);
  // endregion

  /**
   * Run any potential migrations that need to be run on the sheet.
   */
  const runMigrations = useCallback(async () => {
    console.count('runMigrations');

    const token = await getIdToken();
    const eventSource = new EventSource(`${API_ROOT}/migrations?token=${token}`);
    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);

      setMigrating(data.migrating);
      setMigrationMessage(data.message);

      // Check if the task is complete and close the connection
      if (!data.migrating) {
        console.log("Stream closed by server completion.");
        eventSource.close();
      }
    };
    eventSource.onerror = (error) => {
      console.error('EventSource failed:', error);
      eventSource.close();
    };
  }, [getIdToken]);

  // region GET data
  /**
   * Get all expenses from Google Sheet
   * @param rowLimit - Number of rows to return
   */
  const getExpenses = useCallback(async (rowLimit?: number): Promise<void> => {
    console.count('getExpenses');
    const response = await firebaseRequest({
      url: `${API_ROOT}/expenses`,
      method: 'GET',
      params: {
        rows: rowLimit,
      },
    });
    setExpenses(response.data);
    setExpensesLoading(false);
  }, [firebaseRequest]);
  /**
   * Get all exchanges from Google Sheet
   * @param rowLimit - Number of rows to return
   */
  const getExchanges = useCallback(async (rowLimit?: number): Promise<void> => {
    console.count('getExchanges');
    const response = await firebaseRequest({
      url: `${API_ROOT}/exchanges`,
      method: 'GET',
      params: {
        rows: rowLimit,
      },
    });
    setExchanges(response.data);
    setExchangesLoading(false);
  }, [firebaseRequest]);

  /**
   * Get the details of a given spreadsheet/document
   * @param spreadsheetId - The ID of the spreadsheet to retrieve
   */
  const getSpreadsheet = useCallback(async (spreadsheetId?: string): Promise<any> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/sheets/${encodeURIComponent(spreadsheetId || '')}`,
      method: 'GET',
    });
    return response.data;
  }, [firebaseRequest])
  // endregion

  // region ADD/UPDATE
  /**
   * Add/Update expense to Sheet and update state
   * @param expense - The expense data to add or update
   */
  const addExpense = useCallback(async (expense: Partial<Expense>): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/expenses`,
      method: 'POST',
      data: expense,
    });
    if (response.data) {
      const newExpense: Expense = response.data;
      if (expense.rowNumber) {
        // update existing
        await setExpenses((prev) => {
          const newList = [...prev];
          const idx = newList.findIndex((r) => r.rowNumber === expense.rowNumber);
          if (idx > -1) {
            newList[idx] = newExpense;
            newList[idx].rowNumber = expense.rowNumber;
          }
          return newList;
        })
      } else {
        // add new
        await setExpenses(
          (prev) => [newExpense, ...prev]
        );
      }

      // reload expenses/exchanges from sheet asynchronously
      getExpenses(EXPENSES_ROW_LIMIT);
      getExchanges(EXCHANGES_ROW_LIMIT);
    }
  }, [firebaseRequest, getExpenses, getExchanges])

  /**
   * Add/Update exchange to Sheet and update state
   * @param exchange - The exchange data to add or update
   */
  const addExchange = useCallback(async (exchange: Partial<Exchange>): Promise<void> => {
    if (exchanges.length < 1) {
      (exchange as any).StartRow = 2;
    }
    const response = await firebaseRequest({
      url: `${API_ROOT}/exchanges`,
      method: 'POST',
      data: exchange,
    });
    const newExchange: Exchange = response.data;
    if (exchange.rowNumber) {
      // update existing
      /*await setExpenses((prev) => {
        const newList = [...prev];
        const idx = newList.findIndex((r) => r.rowNumber === exchange.rowNumber);
        if (idx > -1) {
          newList[idx] = newExchange;
          newList[idx].rowNumber = exchange.rowNumber;
        }
        return newList;
      })*/
    } else {
      // add new
      await setExchanges(
        (prev) => [newExchange, ...prev]
      );
    }
  }, [firebaseRequest, exchanges])
  // endregion

  // region Accounts
  /**
   * Add a new account to the sheet and update state
   * @param accountName - The name of the account to add
   */
  const addAccount = useCallback(async (accountName: string): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/accounts`,
      method: 'POST',
      data: {
        name: accountName,
      },
    });
    if (response.data) {
      setAccounts(response.data);
    }
  }, [firebaseRequest]);

  /**
   * Update an existing account in the sheet and update state
   * @param oldAccountName - The current name of the account
   * @param newAccountName - The new name for the account
   */
  const updateAccount = useCallback(async (oldAccountName: string, newAccountName: string): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/accounts/${encodeURIComponent(oldAccountName)}`,
      method: 'PUT',
      data: {
        name: newAccountName,
      },
    });
    if (response.data) {
      setAccounts(response.data);
    }
  }, [firebaseRequest]);

  /**
   * Delete an account from the sheet and update state
   * @param accountName - The name of the account to delete
   */
  const deleteAccount = useCallback(async (accountName: string): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/accounts/${encodeURIComponent(accountName)}`,
      method: 'DELETE',
    });
    if (response.data) {
      setAccounts(response.data);
    }
  }, [firebaseRequest]);
  // endregion

  // region Categories
  /**
   * Get all categories and accounts from Google Sheet
   */
  const getCategories = useCallback(async (): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/categories`,
      method: 'GET',
    });
    setCategories(response.data.categories);
    setAccounts(response.data.accounts);
  }, [firebaseRequest]);

  /**
   * Add a new category to the sheet and update state
   * @param categoryName - The name of the category to add
   */
  const addCategory = useCallback(async (categoryName: string): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/categories`,
      method: 'POST',
      data: {
        name: categoryName,
      },
    });
    if (response.data) {
      setCategories(response.data);
    }
  }, [firebaseRequest]);

  /**
   * Update an existing category in the sheet and update state
   * @param oldCategoryName - The current name of the category
   * @param newCategoryName - The new name for the category
   */
  const updateCategory = useCallback(async (oldCategoryName: string, newCategoryName: string): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/categories/${encodeURIComponent(oldCategoryName)}`,
      method: 'PUT',
      data: {
        name: newCategoryName,
      },
    });
    if (response.data) {
      setCategories(response.data);
    }
  }, [firebaseRequest]);

  /**
   * Delete a category from the sheet and update state
   * @param categoryName - The name of the category to delete
   */
  const deleteCategory = useCallback(async (categoryName: string): Promise<void> => {
    const response = await firebaseRequest({
      url: `${API_ROOT}/categories/${encodeURIComponent(categoryName)}`,
      method: 'DELETE',
    });
    if (response.data) {
      setCategories(response.data);
    }
  }, [firebaseRequest]);
  // endregion

  // onLoad
  useEffect(() => {
    const loadData = async() => {
      console.count('loadData');
      await runMigrations();
      await getExpenses(EXPENSES_ROW_LIMIT);
      getExchanges(EXCHANGES_ROW_LIMIT);
      getCategories();
    };
    if (!userLoading && user && user.uid) {
      loadData();
    }
  }, [runMigrations, getExpenses, getExchanges, getCategories, user, userLoading]);

  const context: SheetContextState = {
    // State
    migrating,
    migrationMessage,
    expenses,
    exchanges,
    categories,
    accounts,
    expensesLoading,
    exchangesLoading,
    // Functions
    getExpenses,
    getExchanges,
    getCategories,
    getSpreadsheet,
    addExpense,
    addExchange,
    addAccount,
    updateAccount,
    deleteAccount,
    addCategory,
    updateCategory,
    deleteCategory,
  };
  return <SheetContext.Provider value={context}>
    {children}
  </SheetContext.Provider>
}
